/**
 * Input steps on the wire. The client plays 60 steps a second and sends them
 * three to a message; each is packed into about eight bytes: a flags byte,
 * the three axes to 1/127, what's held as an item index, and the aim to
 * 1/16 of a block as a difference from the step before. The sequence number
 * and dt go only when they aren't the step after the last and INPUT_DT.
 */
import { Reader, Writer } from "@massivewebgames/netsync";
import { INPUT_DT } from "./constants";
import { ITEM_IDS } from "./items";
import type { InputAction, PlayerInput } from "./world/types";

const ACTIONS: readonly (InputAction | null)[] = [null, "grapple", "throw"];
const AIM_SCALE = 16;
const AXIS_SCALE = 127;
/** Flag bits. */
const INTERACT = 1;
const FIRE = 2;
const BRACE = 4;
const ACTION_SHIFT = 3; // an index into ACTIONS
const ODD_DT = 32;
const ODD_SEQ = 64;
/** More steps than this in one message is nonsense (the client sends three). */
const MAX_STEPS = 64;

const axis = (v: number) => Math.round(Math.max(-1, Math.min(1, v || 0)) * AXIS_SCALE);

export function encodeInputs(inputs: readonly PlayerInput[]): Uint8Array {
  const w = new Writer(16 + inputs.length * 12);
  w.uvar(inputs.length);
  let prevSeq = NaN;
  let ax = 0;
  let ay = 0;
  for (const p of inputs) {
    const action = Math.max(0, ACTIONS.indexOf(p.action ?? null));
    const plainDt = Math.abs(p.dt - INPUT_DT) < 1e-9;
    const nextSeq = p.seq === prevSeq + 1;
    w.u8((p.interact ? INTERACT : 0) | (p.fire ? FIRE : 0) | (p.brace ? BRACE : 0) | (action << ACTION_SHIFT) | (plainDt ? 0 : ODD_DT) | (nextSeq ? 0 : ODD_SEQ));
    if (!nextSeq) w.f64(p.seq);
    if (!plainDt) w.f32(p.dt);
    w.i8(axis(p.moveX));
    w.i8(axis(p.moveY));
    w.i8(axis(p.turn));
    w.uvar(p.hold === null ? 0 : ITEM_IDS.indexOf(p.hold) + 1);
    const qx = Math.round((p.aimX || 0) * AIM_SCALE);
    const qy = Math.round((p.aimY || 0) * AIM_SCALE);
    w.svar(qx - ax);
    w.svar(qy - ay);
    ax = qx;
    ay = qy;
    prevSeq = p.seq;
  }
  return w.finish();
}

/** The steps in a message, or null if it isn't one. The server still checks each field's range (see handleInput). */
export function decodeInputs(data: unknown): PlayerInput[] | null {
  if (!(data instanceof Uint8Array)) return null;
  try {
    const r = new Reader(data);
    const n = r.uvar();
    if (n > MAX_STEPS) return null;
    const out: PlayerInput[] = [];
    let seq = NaN;
    let ax = 0;
    let ay = 0;
    for (let k = 0; k < n; k++) {
      const flags = r.u8();
      seq = flags & ODD_SEQ ? r.f64() : seq + 1;
      const dt = flags & ODD_DT ? r.f32() : INPUT_DT;
      const moveX = r.i8() / AXIS_SCALE;
      const moveY = r.i8() / AXIS_SCALE;
      const turn = r.i8() / AXIS_SCALE;
      const hold = r.uvar();
      ax += r.svar();
      ay += r.svar();
      out.push({
        seq,
        dt,
        moveX,
        moveY,
        turn,
        interact: !!(flags & INTERACT),
        brace: !!(flags & BRACE),
        fire: !!(flags & FIRE),
        hold: hold ? (ITEM_IDS[hold - 1] ?? null) : null,
        aimX: ax / AIM_SCALE,
        aimY: ay / AIM_SCALE,
        action: ACTIONS[(flags >> ACTION_SHIFT) & 3] ?? null,
      });
    }
    return out;
  } catch {
    return null;
  }
}
